import { DEFAULT_PAGE_SIZE } from "@/constants";
import { TCreateLeadPayload, TLead, TLeadResponse, TLeadsFilters } from "@/models/leads.model";
import { TRequestStatus } from "@/models/msc.model";
import leadsService from "@/services/leads.service";
import { AnyAction, createSelector, createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";

type LeadsState = {
  initialized: boolean;
  entities: Record<number, TLead>;
  pages: Record<number, { ids: number[] }>;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  filters: TLeadsFilters;
  ui: { status: TRequestStatus };
};

const INITIAL_FILTERS = { page: 1, pageSize: DEFAULT_PAGE_SIZE, query: "" };
const initialState: LeadsState = {
  initialized: false,
  entities: {},
  pages: {},
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
  filters: INITIAL_FILTERS,
  ui: { status: "IDLE" },
};

const leadsSlice = createSlice({
  name: "leads",
  initialState,
  reducers: {
    startLoading(state) {
      state.ui.status = "PENDING";
    },
    gotLeadsByPage(state, action: PayloadAction<TLeadResponse>) {
      const { data: leads, meta } = action.payload;
      const { page, totalCount, totalPages } = meta;

      leads.forEach((lead) => (state.entities[lead.id] = { ...(state.entities[lead.id] ?? {}), ...lead }));
      state.pages[page] = { ids: leads.map((lead) => lead.id) };
      state.currentPage = page;
      state.totalPages = totalPages;
      state.totalCount = totalCount;
      state.initialized = true;
      state.ui.status = "IDLE";
    },
    leadUpdated(state, action: PayloadAction<TLead>) {
      state.entities[action.payload.id] = { ...(state.entities[action.payload.id] ?? {}), ...action.payload };
      state.ui.status = "IDLE";
    },
    leadCreated(state, action: PayloadAction<TLead>) {
      // TODO: Analize what would be the behavior for the pagination
      state.entities[action.payload.id] = action.payload;
      state.totalCount += 1;
      state.ui.status = "IDLE";
    },
    leadDeleted(state, action: PayloadAction<number>) {
      const leadId = action.payload;
      delete state.entities[leadId];
      // Remove from all pages
      Object.keys(state.pages).forEach((page) => {
        const pageNum = parseInt(page);
        state.pages[pageNum].ids = state.pages[pageNum].ids.filter(id => id !== leadId);
      });
      state.totalCount = Math.max(0, state.totalCount - 1);
      state.ui.status = "IDLE";
    },
    rollback(state, action: PayloadAction<TLead>) {
      state.entities[action.payload.id] = action.payload;
      state.ui.status = "IDLE";
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },
    setFilters(state, action: PayloadAction<TLeadsFilters>) {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters(state) {
      state.filters = INITIAL_FILTERS;
    },
    clearPages(state) {
      state.pages = {};
      state.currentPage = 1;
    },
  },
});

const { startLoading, gotLeadsByPage, setCurrentPage, setFilters, resetFilters, clearPages, leadUpdated, leadCreated, leadDeleted, rollback } =
  leadsSlice.actions;
export default leadsSlice.reducer;

//-----THUNK FUNCTIONS-----
export const getLeads =
  (page = 1) =>
    async (dispatch: Dispatch, getState: () => RootState) => {
      const state = getState();
      const { pages, filters } = state.leads;
      const cached = pages[page];
      if (cached) {
        dispatch(setCurrentPage(page)); // if already cached, return directly
        return selectCurrentPageLeads(state);
      }
      dispatch(startLoading());
      const resp = await leadsService.getAll({ ...filters, page });
      dispatch(gotLeadsByPage(resp));
      return resp.data;
    };

export const applyFiltersAndSearch =
  (newFilters: TLeadsFilters) =>
    async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
      const merged: TLeadsFilters = {
        ...getState().leads.filters,
        ...newFilters,
        page: 1,
      };
      dispatch(setFilters(merged));
      dispatch(clearPages());
      await dispatch(getLeads(1) as unknown as AnyAction);
    };

export const resetFiltersAndSearch =
  () =>
    async (dispatch: Dispatch): Promise<void> => {
      dispatch(resetFilters());
      dispatch(clearPages());
      await dispatch(getLeads(1) as unknown as AnyAction);
    };

export const updateLeadById =
  (leadId: number, data: Partial<Omit<TLead, "id">>) => async (dispatch: Dispatch, getState: () => RootState) => {
    const { entities } = getState().leads;
    const cachedLead = entities[leadId];

    // Deep clone cached pages for rollback
    const forRollback = structuredClone(cachedLead);

    // Optimistic update: dispatch updated lead
    dispatch(leadUpdated({ id: leadId, ...data } as TLead));
    try {
      const response = await leadsService.update(leadId, data);
      return response;
    } catch (error) {
      dispatch(rollback(forRollback));
      throw error;
    }
  };

export const createLead = (data: TCreateLeadPayload) => async (dispatch: Dispatch) => {
  dispatch(startLoading());
  const newLead = await leadsService.createLead(data);
  dispatch(leadCreated(newLead));
  return newLead;

};

export const fetchLeadById = (leadId: number) => async (dispatch: Dispatch, getState: () => RootState) => {
  const { entities } = getState().leads;
  const cachedLead = entities[leadId];
  
  // If we already have the full lead data, return it
  if (cachedLead && cachedLead.email !== undefined) {
    return cachedLead;
  }
  
  // Otherwise fetch from API
  dispatch(startLoading());
  const lead = await leadsService.getById(leadId);
  dispatch(leadUpdated(lead));
  return lead;
};

export const deleteLeadById = (leadId: number) => async (dispatch: Dispatch) => {
  await leadsService.deleteLead(leadId);
  dispatch(leadDeleted(leadId));
};

/* Selectors */
const selectEntities = (state: RootState) => state.leads.entities;
const selectPages = (state: RootState) => state.leads.pages;
const selectCurrentPage = (state: RootState) => state.leads.currentPage;

export const selectCurrentPageLeads = createSelector(
  [selectEntities, selectPages, selectCurrentPage],
  (entities, pages, currentPage): TLead[] => {
    const rec = pages[currentPage];
    if (!rec || !rec.ids || rec.ids.length === 0) return [];
    return rec.ids.map((id) => entities[id]).filter(Boolean) as TLead[];
  }
);
